import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiActivity,
  FiEye,
  FiFilm,
  FiGrid,
  FiHome,
  FiSearch,
} from 'react-icons/fi';
import { Card } from '../../../ComponnentsUI/common/Card';
import { NoDataPlaceholder } from '../../../ComponnentsUI/common/NoDataPlaceholder';
import {
  HomeKpisAiCoverage,
  HomeKpisAiCoverageSlice,
  HomeKpisAiMeasurementRow,
  HomeKpisAiStructureRow,
  HomeKpisCoverageItem,
  HomeKpisData,
} from '../../../interfaces/homeKpis';
import {
  ENHANCED_COHORT,
  ENHANCED_FACILITY,
  ENHANCED_STRUCTURES,
  ENHANCED_TRIMESTER,
  EnhancedStructureRow,
} from '../enhancedCoverage';
import { SimpleBarChart } from './HomeKpiCharts';
import { ViewQuickStats } from './HomeKpiWidgets';

type Mode = 'standard' | 'enhanced';
type ConfidenceMode = 'any' | 'high';

const LOW_N_FOOTNOTE = 5;
const DET_PER_FRAME_FLAG = 1.3;

interface Props {
  data: HomeKpisData | undefined;
  enableEnhanced?: boolean;
}

interface NormalizedRow {
  category_id: number | null;
  code: string;
  display_name: string;
  group: string;
  studies_percent_any: number;
  studies_percent_high: number;
  videos_percent: number;
  frames_percent_any: number;
  frames_percent_high: number;
  mean_score: number | null;
  det_per_frame_any: number | null;
  det_per_frame_high: number | null;
  detections_any: number;
  detections_high: number;
  best_sweep_any: { index: number; hit_rate: number } | null;
  best_sweep_high: { index: number; hit_rate: number } | null;
  raw_group_hint?: string;
}

interface NormalizedColumn {
  key: string;
  label: string;
  n: number;
  videos_per_study?: number;
}

interface NormalizedCell {
  key: string;
  percent_any: number;
  percent_high: number;
}

interface NormalizedHeatmap {
  columns: NormalizedColumn[];
  rows: { category_id: number | string; cells: NormalizedCell[] }[];
}

interface View {
  mode: Mode;
  counters: {
    exams: number;
    exams_with_inference: number;
    sweeps: number;
    frames: number;
    structures_detected: number;
    facilities: number;
  };
  structures: NormalizedRow[];
  trimester: NormalizedHeatmap;
  facility: NormalizedHeatmap;
  totalDetections: number;
}

// ---------------- normalization ----------------

function humanizeGroup(raw: string): string {
  if (!raw) return 'other';
  const g = raw.toLowerCase();
  if (g === 'abdomen') return 'abdominal';
  return g;
}

function normalizeStandard(
  slice: HomeKpisAiCoverageSlice
): { view: Omit<View, 'mode'>; groupNames: Set<string> } {
  const structures = slice.structures.map<NormalizedRow>((r) => ({
    category_id: r.category_id,
    code: r.code,
    display_name: r.display_name,
    group: humanizeGroup(r.group),
    studies_percent_any: r.studies_percent_any,
    studies_percent_high: r.studies_percent_high,
    videos_percent: r.videos_percent,
    frames_percent_any: r.frames_percent_any,
    frames_percent_high: r.frames_percent_high,
    mean_score: r.mean_score,
    det_per_frame_any: r.det_per_frame_any,
    det_per_frame_high: r.det_per_frame_high,
    detections_any: r.detections_any,
    detections_high: r.detections_high,
    best_sweep_any: r.best_sweep_any,
    best_sweep_high: r.best_sweep_high,
  }));

  const structureLookup = new Map(structures.map((s) => [s.category_id!, s]));

  const trimester: NormalizedHeatmap = {
    columns: slice.trimester_heatmap.columns.map((c) => ({
      key: c.key,
      label: c.label,
      n: c.n,
    })),
    rows: slice.trimester_heatmap.rows.map((r) => ({
      category_id: r.category_id,
      cells: r.cells,
    })),
  };
  const facility: NormalizedHeatmap = {
    columns: slice.facility_heatmap.columns.map((c) => ({
      key: c.key,
      label: c.label,
      n: c.n,
      videos_per_study: c.videos_per_study,
    })),
    rows: slice.facility_heatmap.rows.map((r) => ({
      category_id: r.category_id,
      cells: r.cells,
    })),
  };

  const totalDetections = structures.reduce((sum, r) => sum + r.detections_any, 0);

  const groupNames = new Set(structures.map((s) => s.group));

  return {
    view: {
      counters: slice.counters,
      structures,
      trimester,
      facility,
      totalDetections,
    },
    groupNames,
  };
}

function normalizeEnhanced(
  displayNamesByCode: Map<string, { display_name: string; category_id: number }>
): Omit<View, 'mode'> {
  const structures: NormalizedRow[] = ENHANCED_STRUCTURES.map((r) => {
    const catalogue = displayNamesByCode.get(r.structure_code);
    const totalDetections = r.total_detections;
    return {
      category_id: catalogue?.category_id ?? null,
      code: r.structure_code,
      display_name: catalogue?.display_name ?? enhanceDisplay(r.ds_class),
      group: humanizeGroup(r.db_category_group),
      studies_percent_any: r.studies_pct,
      studies_percent_high: r.studies_pct, // enhanced: no separate high slice for studies
      videos_percent: r.videos_pct,
      frames_percent_any: r.frames_pct_any,
      frames_percent_high: r.frames_pct_conf05,
      mean_score: r.mean_score,
      det_per_frame_any: r.det_per_frame,
      det_per_frame_high: r.det_per_frame,
      detections_any: totalDetections,
      detections_high: totalDetections, // enhanced lacks a >=0.5 count
      best_sweep_any:
        r.best_sweep != null && r.best_sweep_pct != null
          ? { index: r.best_sweep, hit_rate: r.best_sweep_pct }
          : null,
      best_sweep_high:
        r.best_sweep != null && r.best_sweep_pct != null
          ? { index: r.best_sweep, hit_rate: r.best_sweep_pct }
          : null,
      raw_group_hint: r.ds_group,
    };
  });

  // trimester
  const triColumns: NormalizedColumn[] = [
    { key: 't1', label: 'T1 (<14w)', n: ENHANCED_COHORT.trimester_n.t1 },
    { key: 't2', label: 'T2 (14-28w)', n: ENHANCED_COHORT.trimester_n.t2 },
    { key: 't3', label: 'T3 (>=28w)', n: ENHANCED_COHORT.trimester_n.t3 },
  ];
  if (ENHANCED_COHORT.trimester_n.unknown > 0) {
    triColumns.push({
      key: 'unknown',
      label: 'Unknown GA',
      n: ENHANCED_COHORT.trimester_n.unknown,
    });
  }
  const trimester: NormalizedHeatmap = {
    columns: triColumns,
    rows: ENHANCED_TRIMESTER.map((r) => ({
      category_id: r.structure_code,
      cells: [
        { key: 't1', percent_any: r.t1_lt14w_pct, percent_high: r.t1_lt14w_pct },
        { key: 't2', percent_any: r.t2_14_28w_pct, percent_high: r.t2_14_28w_pct },
        { key: 't3', percent_any: r.t3_ge28w_pct, percent_high: r.t3_ge28w_pct },
      ],
    })),
  };

  // facility
  const facilityColumns: NormalizedColumn[] = Object.entries(
    ENHANCED_COHORT.facility_labels
  ).map(([key, label]) => ({
    key,
    label,
    n: ENHANCED_COHORT.facility_n[key as keyof typeof ENHANCED_COHORT.facility_n],
  }));
  const facility: NormalizedHeatmap = {
    columns: facilityColumns,
    rows: ENHANCED_FACILITY.map((r) => ({
      category_id: r.structure_code,
      cells: facilityColumns.map((c) => {
        const v = (r as unknown as Record<string, number>)[c.key];
        return { key: c.key, percent_any: v, percent_high: v };
      }),
    })),
  };

  const totalDetections = structures.reduce((s, r) => s + r.detections_any, 0);
  const structuresDetected = structures.filter((s) => s.detections_any > 0).length;

  return {
    counters: {
      exams: ENHANCED_COHORT.studies,
      exams_with_inference: ENHANCED_COHORT.studies,
      sweeps: ENHANCED_COHORT.videos,
      frames: ENHANCED_COHORT.frames,
      structures_detected: structuresDetected,
      facilities: ENHANCED_COHORT.facilities,
    },
    structures,
    trimester,
    facility,
    totalDetections,
  };
}

function enhanceDisplay(ds_class: string): string {
  const words = ds_class.replace(/_/g, ' ').split(' ');
  return words
    .filter(Boolean)
    .map((w) => (w.length <= 2 ? w : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
}

// ---------------- root ----------------

export const AiCoverageTab: React.FC<Props> = ({ data, enableEnhanced = false }) => {
  const { t } = useTranslation('Dashboard');
  const hasStandard = !!data?.ai_coverage;
  const [mode, setMode] = useState<Mode>(enableEnhanced ? 'enhanced' : 'standard');
  React.useEffect(() => {
    if (!enableEnhanced && mode === 'enhanced') {
      setMode('standard');
    }
  }, [enableEnhanced, mode]);
  const [completeOnly, setCompleteOnly] = useState(false);
  const [confidence, setConfidence] = useState<ConfidenceMode>('any');

  // catalogue lookup for Enhanced -> readable display names
  const catalogueByCode = useMemo(() => {
    const m = new Map<string, { display_name: string; category_id: number }>();
    if (data?.ai_coverage) {
      const slice = data.ai_coverage.all;
      for (const s of slice.structures) {
        m.set(s.code, { display_name: s.display_name, category_id: s.category_id });
      }
    }
    return m;
  }, [data]);

  const view: View = useMemo(() => {
    if (mode === 'enhanced') {
      return { mode: 'enhanced', ...normalizeEnhanced(catalogueByCode) };
    }
    if (!data?.ai_coverage) {
      return {
        mode: 'standard',
        counters: {
          exams: 0,
          exams_with_inference: 0,
          sweeps: 0,
          frames: 0,
          structures_detected: 0,
          facilities: 0,
        },
        structures: [],
        trimester: { columns: [], rows: [] },
        facility: { columns: [], rows: [] },
        totalDetections: 0,
      };
    }
    const slice = completeOnly ? data.ai_coverage.complete_only : data.ai_coverage.all;
    const { view } = normalizeStandard(slice);
    return { mode: 'standard', ...view };
  }, [mode, completeOnly, data, catalogueByCode]);

  const totalExamsAll = data?.ai_coverage?.all.counters.exams ?? 0;

  return (
    <div className="space-y-4 sm:space-y-6">
      <ModeAndControls
        mode={mode}
        onModeChange={setMode}
        completeOnly={completeOnly}
        onCompleteOnlyChange={setCompleteOnly}
        confidence={confidence}
        onConfidenceChange={setConfidence}
        currentExams={view.counters.exams}
        totalExams={totalExamsAll}
        hasStandard={hasStandard}
        enableEnhanced={enableEnhanced}
      />

      <CounterStrip counters={view.counters} denominator={ENHANCED_STRUCTURES.length} />

      {view.totalDetections === 0 ? (
        <NoDataPlaceholder
          className="h-40"
          message={t('kpis.aiCoverage.empty')}
        />
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2 sm:gap-6">
            <Card title={t('kpis.aiCoverage.viewsPanel.quickStats')}>
              <LiveBadge />
              {data ? (
                <ViewQuickStats stats={pickViewsSlice(data).quick_stats} />
              ) : (
                <NoDataPlaceholder className="h-40" message={t('kpis.aiCoverage.empty')} />
              )}
            </Card>
            <Card title={t('kpis.aiCoverage.viewsPanel.perExamTitle')}>
              <LiveBadge />
              {data ? (
                <>
                  <SimpleBarChart
                    data={pickViewsSlice(data).distinct_view_buckets}
                    label={t('analytics.exams')}
                    color="#06B6D4"
                  />
                  <p className="mt-2 text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                    {t('kpis.aiCoverage.viewsPanel.perExamFootnote')}
                  </p>
                </>
              ) : null}
            </Card>
          </div>

          <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-2 sm:gap-6">
            <Card title={t('kpis.aiCoverage.viewsPanel.coverageTitle')}>
              <LiveBadge />
              <ViewCoverageList rows={data ? pickViewsSlice(data).coverage : []} />
            </Card>
            <Card title={t('kpis.aiCoverage.measurements.title')}>
              <LiveBadge />
              <MeasurementList rows={data ? pickMeasurements(data) : []} />
            </Card>
          </div>

          <StructureCoverageCard view={view} confidence={confidence} />

          <Card title={t('kpis.aiCoverage.heatmap.trimester.title')}>
            <HeatmapView
              heatmap={view.trimester}
              structures={view.structures}
              confidence={confidence}
              columnHeader={t('kpis.aiCoverage.heatmap.columnTrimester')}
            />
          </Card>
          <Card title={t('kpis.aiCoverage.heatmap.facility.title')}>
            <HeatmapView
              heatmap={view.facility}
              structures={view.structures}
              confidence={confidence}
              columnHeader={t('kpis.aiCoverage.heatmap.columnFacility')}
            />
          </Card>
        </>
      )}
    </div>
  );
};

function pickViewsSlice(data: HomeKpisData): HomeKpisAiCoverageSlice['views_panel'] {
  return data.ai_coverage?.all.views_panel
    ?? { quick_stats: data.ai.view_quick_stats, distinct_view_buckets: data.ai.distinct_view_buckets, coverage: data.ai.view_coverage };
}

function pickMeasurements(data: HomeKpisData): HomeKpisAiMeasurementRow[] {
  return (
    data.ai_coverage?.all.measurements
    ?? (data.ai.measurement_coverage || []).map<HomeKpisAiMeasurementRow>((r) => ({
      code: r.name,
      name: r.name,
      studies: r.studies,
      detections: r.detections,
      coverage_percent: r.coverage_percent,
    }))
  );
}

// ---------------- controls ----------------

const ModeAndControls: React.FC<{
  mode: Mode;
  onModeChange: (next: Mode) => void;
  completeOnly: boolean;
  onCompleteOnlyChange: (next: boolean) => void;
  confidence: ConfidenceMode;
  onConfidenceChange: (next: ConfidenceMode) => void;
  currentExams: number;
  totalExams: number;
  hasStandard: boolean;
  enableEnhanced: boolean;
}> = ({
  mode,
  onModeChange,
  completeOnly,
  onCompleteOnlyChange,
  confidence,
  onConfidenceChange,
  currentExams,
  totalExams,
  hasStandard,
  enableEnhanced,
}) => {
  const { t } = useTranslation('Dashboard');
  const enhanced = mode === 'enhanced';
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
        <div className="flex flex-col text-xs text-gray-500 dark:text-gray-400">
          <span className="font-semibold uppercase tracking-wide text-title-page-color-100 dark:text-white">
            {t('kpis.aiCoverage.controls.title')}
          </span>
          <span>{t('kpis.aiCoverage.controls.subtitle')}</span>
        </div>
        <div className="flex flex-wrap items-center gap-4">
          {enableEnhanced && (
            <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
              <span>{t('kpis.aiCoverage.controls.dataSource')}</span>
              <SegmentedControl<Mode>
                value={mode}
                onChange={(m) => onModeChange(m)}
                options={[
                  {
                    value: 'standard',
                    label: t('kpis.aiCoverage.controls.standard'),
                    disabled: !hasStandard,
                  },
                  {
                    value: 'enhanced',
                    label: t('kpis.aiCoverage.controls.enhanced'),
                  },
                ]}
              />
            </div>
          )}
          <label
            className={`flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300 ${
              enhanced ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title={enhanced ? t('kpis.aiCoverage.controls.completeOnlyDisabledTip') : undefined}
          >
            <input
              type="checkbox"
              checked={enhanced ? false : completeOnly}
              disabled={enhanced}
              onChange={(e) => onCompleteOnlyChange(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-blue-600"
            />
            <span>{t('kpis.aiCoverage.controls.completeOnly')}</span>
            <span className="rounded-full bg-gray-100 dark:bg-gray-700 px-2 py-0.5 font-mono text-[11px] text-gray-500 dark:text-gray-300">
              {t('kpis.aiCoverage.controls.nOfTotal', {
                current: currentExams.toLocaleString(),
                total: (enhanced
                  ? ENHANCED_COHORT.studies_registered
                  : totalExams
                ).toLocaleString(),
              })}
            </span>
          </label>
          <div className="flex items-center gap-2 text-xs font-semibold text-gray-600 dark:text-gray-300">
            <span>{t('kpis.aiCoverage.controls.confidence')}</span>
            <SegmentedControl<ConfidenceMode>
              value={confidence}
              onChange={onConfidenceChange}
              options={[
                { value: 'any', label: t('kpis.aiCoverage.controls.confAny') },
                { value: 'high', label: t('kpis.aiCoverage.controls.confHigh') },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

function SegmentedControl<T extends string>({
  value,
  onChange,
  options,
}: {
  value: T;
  onChange: (next: T) => void;
  options: { value: T; label: string; disabled?: boolean }[];
}) {
  return (
    <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 bg-card-color-200 dark:bg-gray-900/40 p-0.5 text-xs">
      {options.map((opt) => {
        const selected = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={opt.disabled}
            onClick={() => opt.disabled || onChange(opt.value)}
            className={`rounded-md px-3 py-1 font-semibold transition-colors ${
              selected
                ? 'bg-blue-600 text-white shadow-sm'
                : opt.disabled
                  ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

const LiveBadge: React.FC = () => null;

// ---------------- section A: counters ----------------

const CounterStrip: React.FC<{
  counters: View['counters'];
  denominator: number;
}> = ({ counters, denominator }) => {
  const { t } = useTranslation('Dashboard');
  const items = [
    {
      icon: <FiActivity />,
      label: t('kpis.aiCoverage.counters.examsInferred'),
      value: counters.exams_with_inference.toLocaleString(),
      accent: 'text-blue-600 dark:text-blue-400',
    },
    {
      icon: <FiFilm />,
      label: t('kpis.aiCoverage.counters.sweeps'),
      value: counters.sweeps.toLocaleString(),
      accent: 'text-cyan-600 dark:text-cyan-400',
    },
    {
      icon: <FiGrid />,
      label: t('kpis.aiCoverage.counters.frames'),
      value: counters.frames.toLocaleString(),
      accent: 'text-purple-600 dark:text-purple-400',
    },
    {
      icon: <FiEye />,
      label: t('kpis.aiCoverage.counters.structuresDetected'),
      value: `${counters.structures_detected} / ${denominator}`,
      accent: 'text-emerald-600 dark:text-emerald-400',
    },
    {
      icon: <FiHome />,
      label: t('kpis.aiCoverage.counters.facilities'),
      value: counters.facilities.toLocaleString(),
      accent: 'text-amber-600 dark:text-amber-400',
    },
  ];
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {items.map((it) => (
        <div
          key={it.label}
          className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-800 p-4"
        >
          <div className={`mb-3 inline-flex rounded-lg bg-card-color-200 dark:bg-gray-900/40 p-2 text-lg ${it.accent}`}>
            {it.icon}
          </div>
          <div className="text-xs font-semibold text-gray-500 dark:text-gray-400">
            {it.label}
          </div>
          <div className="mt-1 text-xl font-bold text-title-page-color-100 dark:text-white tabular-nums">
            {it.value}
          </div>
        </div>
      ))}
    </div>
  );
};

// ---------------- section B: findings ----------------

interface Finding {
  key: 'acquisition_gap' | 'cardiac_long_tail' | 'silent_structures' | 'distal_limb_reversal';
  bigNumber: string;
  detail: React.ReactNode;
}

function computeFindings(view: View, t: (k: string, o?: Record<string, unknown>) => string): Finding[] {
  const out: Finding[] = [];

  // 1. Acquisition gap (LEAD)
  const facColsWithVideos = view.facility.columns.filter((c) => c.videos_per_study != null || view.mode === 'enhanced');
  const includedFacCols = view.mode === 'enhanced'
    ? view.facility.columns.map((c) => ({ ...c, videos_per_study: (c.key === 'csr2_aghbala' ? 4.9 : c.key === 'csr2_moulay_bouazza' ? 6.5 : c.key === 'csr1_boutferda' ? 6.7 : c.key === 'csu_el_ksiba' ? 6.8 : c.key === 'csr2_anergui' ? 7.0 : 6.9) }))
    : facColsWithVideos;

  if (includedFacCols.length >= 2) {
    // mean coverage per facility across 56 structures (from the heatmap)
    const perFacilityMean = new Map<string, number>();
    for (const col of includedFacCols) {
      const values: number[] = [];
      for (const row of view.facility.rows) {
        const cell = row.cells.find((c) => c.key === col.key);
        if (cell) values.push(cell.percent_any);
      }
      if (values.length) perFacilityMean.set(col.key, values.reduce((a, b) => a + b, 0) / values.length);
    }
    // weighted overall = sum(mean_i * n_i) / sum(n_i)
    const totalN = includedFacCols.reduce((s, c) => s + c.n, 0);
    let weighted = 0;
    for (const col of includedFacCols) {
      weighted += (perFacilityMean.get(col.key) ?? 0) * col.n;
    }
    const cohortMean = totalN ? weighted / totalN : 0;
    // identify lowest videos-per-study facility
    let weakest: typeof includedFacCols[number] | null = null;
    for (const c of includedFacCols) {
      const vps = c.videos_per_study ?? Infinity;
      if (!weakest || vps < (weakest.videos_per_study ?? Infinity)) weakest = c;
    }
    if (weakest) {
      const withoutN = includedFacCols.filter((c) => c.key !== weakest!.key);
      const withoutTotalN = withoutN.reduce((s, c) => s + c.n, 0);
      const withoutWeighted = withoutN.reduce(
        (s, c) => s + (perFacilityMean.get(c.key) ?? 0) * c.n,
        0
      );
      const withoutMean = withoutTotalN ? withoutWeighted / withoutTotalN : 0;
      const weakestMean = perFacilityMean.get(weakest.key) ?? 0;
      const share = totalN ? Math.round((weakest.n / totalN) * 100) : 0;
      if (withoutMean - cohortMean > 3) {
        out.push({
          key: 'acquisition_gap',
          bigNumber: `+${(withoutMean - cohortMean).toFixed(1)} pp`,
          detail: (
            <div className="text-xs leading-relaxed text-gray-600 dark:text-gray-300 space-y-1">
              <p>
                {t('kpis.aiCoverage.findings.acquisition_gap.body', {
                  facility: weakest.label,
                  weakestMean: weakestMean.toFixed(1),
                  cohortMean: cohortMean.toFixed(1),
                  withoutMean: withoutMean.toFixed(1),
                  share,
                  weakestVideos: (weakest.videos_per_study ?? 0).toFixed(1),
                })}
              </p>
              {view.mode === 'enhanced' && (
                <p className="text-gray-500 dark:text-gray-400 text-[11px]">
                  {t('kpis.aiCoverage.findings.acquisition_gap.enhancedDetail')}
                </p>
              )}
            </div>
          ),
        });
      }
    }
  }

  // 2. Cardiac long tail
  if (view.structures.length) {
    const overallMean =
      view.structures.reduce((s, r) => s + r.studies_percent_any, 0) / view.structures.length;
    const cardiac = view.structures.filter((r) => r.group === 'cardiac');
    if (cardiac.length) {
      const cardiacMean =
        cardiac.reduce((s, r) => s + r.studies_percent_any, 0) / cardiac.length;
      if (overallMean > 0 && cardiacMean < 0.6 * overallMean) {
        out.push({
          key: 'cardiac_long_tail',
          bigNumber: `${cardiacMean.toFixed(1)}%`,
          detail: (
            <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
              {t('kpis.aiCoverage.findings.cardiac_long_tail.body', {
                cardiacMean: cardiacMean.toFixed(1),
                overallMean: overallMean.toFixed(1),
                n: cardiac.length,
              })}
            </p>
          ),
        });
      }
    }
  }

  // 3. Silent structures (only 1-10)
  const silent = view.structures.filter((r) => r.detections_any === 0);
  if (silent.length >= 1 && silent.length <= 10) {
    const names = silent.map((s) => s.display_name).join(', ');
    out.push({
      key: 'silent_structures',
      bigNumber: silent.length.toLocaleString(),
      detail: (
        <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
          {t('kpis.aiCoverage.findings.silent_structures.body', { names })}
        </p>
      ),
    });
  }

  // 4. Distal limb reversal - structures with T3 <= T2 - 20pp
  if (view.trimester.columns.length >= 3) {
    const t2Key = 't2';
    const t3Key = 't3';
    const structureByKey = new Map<string | number, NormalizedRow>();
    view.structures.forEach((s) => {
      structureByKey.set(view.mode === 'enhanced' ? s.code : (s.category_id ?? -1), s);
    });
    const reversals: { name: string; t2: number; t3: number }[] = [];
    for (const row of view.trimester.rows) {
      const t2Cell = row.cells.find((c) => c.key === t2Key);
      const t3Cell = row.cells.find((c) => c.key === t3Key);
      if (!t2Cell || !t3Cell) continue;
      const struct = structureByKey.get(row.category_id);
      if (!struct) continue;
      if (t2Cell.percent_any >= 20 && t2Cell.percent_any - t3Cell.percent_any >= 20) {
        reversals.push({
          name: struct.display_name,
          t2: t2Cell.percent_any,
          t3: t3Cell.percent_any,
        });
      }
    }
    if (reversals.length) {
      reversals.sort((a, b) => (b.t2 - b.t3) - (a.t2 - a.t3));
      const top = reversals.slice(0, 3);
      out.push({
        key: 'distal_limb_reversal',
        bigNumber: reversals.length.toLocaleString(),
        detail: (
          <p className="text-xs leading-relaxed text-gray-600 dark:text-gray-300">
            {t('kpis.aiCoverage.findings.distal_limb_reversal.body', {
              examples: top.map((r) => `${r.name} ${r.t2.toFixed(1)} → ${r.t3.toFixed(1)}`).join('; '),
            })}
          </p>
        ),
      });
    }
  }

  return out;
}

const FINDING_TITLES: Record<Finding['key'], string> = {
  acquisition_gap: 'kpis.aiCoverage.findings.acquisition_gap.title',
  cardiac_long_tail: 'kpis.aiCoverage.findings.cardiac_long_tail.title',
  silent_structures: 'kpis.aiCoverage.findings.silent_structures.title',
  distal_limb_reversal: 'kpis.aiCoverage.findings.distal_limb_reversal.title',
};

const FindingsRow: React.FC<{ view: View }> = ({ view }) => {
  const { t } = useTranslation('Dashboard');
  const findings = useMemo(() => computeFindings(view, t), [view, t]);
  if (!findings.length) return null;
  const cols = Math.min(findings.length, 4);
  return (
    <div
      className={`grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-${cols}`}
      style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
    >
      {findings.map((f, idx) => (
        <div
          key={f.key}
          className={`flex flex-col rounded-xl border p-4 ${
            idx === 0
              ? 'border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20'
              : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
          }`}
        >
          <div className={`text-xs font-semibold uppercase tracking-wide ${
            idx === 0 ? 'text-amber-800 dark:text-amber-200' : 'text-gray-500 dark:text-gray-400'
          }`}>
            {t(FINDING_TITLES[f.key])}
          </div>
          <div className="mt-1 text-3xl font-bold text-title-page-color-100 dark:text-white tabular-nums">
            {f.bigNumber}
          </div>
          <div className="mt-2">{f.detail as unknown as string}</div>
        </div>
      ))}
    </div>
  );
};

// ---------------- section D: structure table ----------------

type SortKey =
  | 'studies_percent'
  | 'videos_percent'
  | 'frames_any'
  | 'frames_high'
  | 'mean_score'
  | 'det_per_frame'
  | 'detections'
  | 'group'
  | 'name'
  | 'best_sweep';

const StructureCoverageCard: React.FC<{
  view: View;
  confidence: ConfidenceMode;
}> = ({ view, confidence }) => {
  const { t } = useTranslation('Dashboard');
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('studies_percent');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const STRUCTURE_PAGE_SIZE = 12;

  const rows = useMemo(() => {
    const query = search.trim().toLowerCase();
    let filtered = view.structures;
    if (groupFilter !== 'all') {
      filtered = filtered.filter((r) => r.group === groupFilter);
    }
    if (query) {
      filtered = filtered.filter(
        (r) =>
          r.display_name.toLowerCase().includes(query) ||
          r.code.toLowerCase().includes(query)
      );
    }
    const withScore = filtered.map((row) => ({ row, hasDetections: row.detections_any > 0 }));
    withScore.sort((a, b) => {
      if (a.hasDetections !== b.hasDetections) return a.hasDetections ? -1 : 1;
      const sign = sortDir === 'asc' ? 1 : -1;
      const av = pickSort(a.row, sortKey, confidence);
      const bv = pickSort(b.row, sortKey, confidence);
      if (typeof av === 'string' && typeof bv === 'string') return av.localeCompare(bv) * sign;
      return (((av as number) || 0) - ((bv as number) || 0)) * sign;
    });
    return withScore.map((x) => x.row);
  }, [view.structures, groupFilter, search, sortKey, sortDir, confidence]);

  const pageCount = Math.max(1, Math.ceil(rows.length / STRUCTURE_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * STRUCTURE_PAGE_SIZE, (safePage + 1) * STRUCTURE_PAGE_SIZE);
  React.useEffect(() => {
    setPage(0);
  }, [groupFilter, search, sortKey, sortDir, confidence]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'name' || key === 'group' ? 'asc' : 'desc');
    }
  };

  return (
    <Card title={t('kpis.aiCoverage.table.title', { count: rows.length })}>
      <StructureFilters
        structures={view.structures}
        groupFilter={groupFilter}
        onGroupChange={setGroupFilter}
        search={search}
        onSearchChange={setSearch}
      />

      {rows.length === 0 ? (
        <NoDataPlaceholder className="h-40" message={t('kpis.aiCoverage.table.empty')} />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-left text-sm">
              <thead className="bg-gray-50 dark:bg-gray-900/40">
                <tr className="border-b border-gray-200 dark:border-gray-700 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
                  <ThSort label={t('kpis.aiCoverage.table.colStructure')} sortKey="name" active={sortKey} dir={sortDir} onSort={toggleSort} />
                  <ThSort label={t('kpis.aiCoverage.table.colGroup')} sortKey="group" active={sortKey} dir={sortDir} onSort={toggleSort} />
                  <ThSort label={t('kpis.aiCoverage.table.colStudies')} sortKey="studies_percent" active={sortKey} dir={sortDir} onSort={toggleSort} />
                  <ThSort label={t('kpis.aiCoverage.table.colVideos')} sortKey="videos_percent" active={sortKey} dir={sortDir} onSort={toggleSort} />
                  <ThSort label={t('kpis.aiCoverage.table.colFramesAny')} sortKey="frames_any" active={sortKey} dir={sortDir} onSort={toggleSort} highlighted={confidence === 'any'} />
                  <ThSort label={t('kpis.aiCoverage.table.colFramesHigh')} sortKey="frames_high" active={sortKey} dir={sortDir} onSort={toggleSort} highlighted={confidence === 'high'} />
                  <ThSort label={t('kpis.aiCoverage.table.colMeanScore')} sortKey="mean_score" active={sortKey} dir={sortDir} onSort={toggleSort} />
                  <ThSort label={t('kpis.aiCoverage.table.colDetPerFrame')} sortKey="det_per_frame" active={sortKey} dir={sortDir} onSort={toggleSort} />
                  <ThSort label={t('kpis.aiCoverage.table.colDetections')} sortKey="detections" active={sortKey} dir={sortDir} onSort={toggleSort} />
                  <ThSort label={t('kpis.aiCoverage.table.colBestSweep')} sortKey="best_sweep" active={sortKey} dir={sortDir} onSort={toggleSort} />
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r) => (
                  <StructureRow key={`${r.code}-${r.category_id ?? 'x'}`} row={r} confidence={confidence} />
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-2">
            <Pager
              page={safePage}
              pageCount={pageCount}
              totalCount={rows.length}
              pageSize={STRUCTURE_PAGE_SIZE}
              onChange={setPage}
              unit={t('kpis.aiCoverage.pager.structures')}
            />
          </div>
        </>
      )}
      <p className="mt-3 text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
        {t('kpis.aiCoverage.table.scoreFootnote')}
      </p>
    </Card>
  );
};

const ThSort: React.FC<{
  label: string;
  sortKey: SortKey;
  active: SortKey;
  dir: 'asc' | 'desc';
  onSort: (key: SortKey) => void;
  highlighted?: boolean;
}> = ({ label, sortKey, active, dir, onSort, highlighted }) => {
  const isActive = active === sortKey;
  return (
    <th className={`px-3 py-2 font-semibold ${highlighted ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-200' : ''}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 hover:text-title-page-color-100 dark:hover:text-white"
      >
        <span>{label}</span>
        <span className={`text-[10px] ${isActive ? 'opacity-100' : 'opacity-30'}`}>
          {isActive && dir === 'asc' ? '▲' : '▼'}
        </span>
      </button>
    </th>
  );
};

const StructureRow: React.FC<{ row: NormalizedRow; confidence: ConfidenceMode }> = ({ row, confidence }) => {
  const studiesPct = confidence === 'high' ? row.studies_percent_high : row.studies_percent_any;
  const framesPctAny = row.frames_percent_any;
  const framesPctHigh = row.frames_percent_high;
  const detPerFrame = confidence === 'high' ? row.det_per_frame_high : row.det_per_frame_any;
  const detections = confidence === 'high' ? row.detections_high : row.detections_any;
  const bestSweep = confidence === 'high' ? row.best_sweep_high : row.best_sweep_any;

  const zero = row.detections_any === 0;
  const flagged = detPerFrame != null && detPerFrame > DET_PER_FRAME_FLAG;

  const dash = <span className="text-gray-300 dark:text-gray-600">—</span>;

  return (
    <tr className={`border-b border-gray-50 dark:border-gray-800 ${zero ? 'bg-gray-50/60 dark:bg-gray-900/30' : ''}`}>
      <td className="px-3 py-2 min-w-[220px]">
        <div className={`font-medium ${zero ? 'text-gray-500 dark:text-gray-500' : 'text-title-page-color-100 dark:text-white'}`}>
          {row.display_name}
        </div>
      </td>
      <td className="px-3 py-2">
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: groupColor(row.group) }} />
          {row.group}
        </span>
      </td>
      <td className="px-3 py-2 min-w-[140px]">
        <InlineBar value={studiesPct} color="#3B82F6" />
      </td>
      <td className="px-3 py-2 min-w-[140px]">
        <InlineBar value={row.videos_percent} color="#06B6D4" />
      </td>
      <td className={`px-3 py-2 tabular-nums text-gray-700 dark:text-gray-200 ${confidence === 'any' ? 'bg-blue-50/60 dark:bg-blue-900/15' : ''}`}>
        {zero ? dash : `${framesPctAny.toFixed(1)}%`}
      </td>
      <td className={`px-3 py-2 tabular-nums text-gray-700 dark:text-gray-200 ${confidence === 'high' ? 'bg-blue-50/60 dark:bg-blue-900/15' : ''}`}>
        {zero ? dash : `${framesPctHigh.toFixed(1)}%`}
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-200">
        {row.mean_score == null ? dash : row.mean_score.toFixed(3)}
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-200">
        {detPerFrame == null ? (
          dash
        ) : (
          <span className={flagged ? 'text-amber-700 dark:text-amber-400 font-semibold' : ''}>
            {detPerFrame.toFixed(2)}
          </span>
        )}
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-200">
        {zero ? (
          <span className="rounded-md bg-gray-200 dark:bg-gray-700 px-2 py-0.5 text-[10px] text-gray-600 dark:text-gray-300">
            0 detections
          </span>
        ) : (
          detections.toLocaleString()
        )}
      </td>
      <td className="px-3 py-2 tabular-nums text-gray-700 dark:text-gray-200">
        {bestSweep ? `sweep ${bestSweep.index} · ${bestSweep.hit_rate.toFixed(1)}%` : dash}
      </td>
    </tr>
  );
};

const StructureFilters: React.FC<{
  structures: NormalizedRow[];
  groupFilter: string;
  onGroupChange: (g: string) => void;
  search: string;
  onSearchChange: (s: string) => void;
}> = ({ structures, groupFilter, onGroupChange, search, onSearchChange }) => {
  const { t } = useTranslation('Dashboard');
  const groups = useMemo(() => {
    const buckets = new Map<string, number>();
    structures.forEach((s) => buckets.set(s.group, (buckets.get(s.group) || 0) + 1));
    return Array.from(buckets.entries()).sort(([, a], [, b]) => b - a);
  }, [structures]);
  const chips = [
    { key: 'all', label: t('kpis.aiCoverage.table.groupAll'), count: structures.length, color: '#94A3B8' },
    ...groups.map(([key, count]) => ({
      key,
      label: t(`kpis.aiCoverage.groups.${key}`, humanizeCap(key)),
      count,
      color: groupColor(key),
    })),
  ];
  return (
    <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
      <div className="flex flex-wrap items-center gap-1.5">
        {chips.map((chip) => {
          const selected = groupFilter === chip.key;
          return (
            <button
              key={chip.key}
              type="button"
              onClick={() => onGroupChange(chip.key)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold transition-colors ${
                selected
                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-200'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-card-color-200 dark:hover:bg-gray-700'
              }`}
            >
              <span className="h-2 w-2 rounded-full" style={{ backgroundColor: chip.color }} />
              {chip.label}
              <span className="rounded-full bg-white/60 dark:bg-gray-800/60 px-1.5 py-0.5 text-[10px] font-mono">
                {chip.count}
              </span>
            </button>
          );
        })}
      </div>
      <div className="relative flex items-center">
        <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={t('kpis.aiCoverage.table.searchPlaceholder')}
          className="w-64 rounded-md border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 py-1.5 pl-9 pr-3 text-xs text-gray-700 dark:text-gray-200 placeholder:text-gray-400"
        />
      </div>
    </div>
  );
};

function filterStructureRow(
  row: { display_name: string; code: string; group: string },
  groupFilter: string,
  searchQuery: string
): boolean {
  if (groupFilter !== 'all' && row.group !== groupFilter) return false;
  if (searchQuery) {
    const q = searchQuery.trim().toLowerCase();
    if (
      !row.display_name.toLowerCase().includes(q) &&
      !row.code.toLowerCase().includes(q)
    )
      return false;
  }
  return true;
}

const Pager: React.FC<{
  page: number;
  pageCount: number;
  totalCount: number;
  pageSize: number;
  onChange: (p: number) => void;
  unit: string;
}> = ({ page, pageCount, totalCount, pageSize, onChange, unit }) => {
  const { t } = useTranslation('Dashboard');
  if (pageCount <= 1) {
    return (
      <div className="text-[11px] text-gray-500 dark:text-gray-400">
        {totalCount} {unit}
      </div>
    );
  }
  const start = page * pageSize + 1;
  const end = Math.min(totalCount, (page + 1) * pageSize);
  const btn =
    'inline-flex h-7 items-center rounded-md border border-gray-200 dark:border-gray-700 px-2.5 text-[11px] font-semibold text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-40 disabled:cursor-not-allowed';
  return (
    <div className="flex items-center justify-between gap-3 text-[11px] text-gray-500 dark:text-gray-400">
      <span className="tabular-nums">
        {start}-{end} {t('kpis.aiCoverage.pager.of')} {totalCount} {unit}
      </span>
      <div className="flex items-center gap-1.5">
        <button type="button" className={btn} onClick={() => onChange(page - 1)} disabled={page === 0}>
          {t('kpis.aiCoverage.pager.prev')}
        </button>
        <span className="px-1 tabular-nums text-gray-500 dark:text-gray-400">
          {page + 1} / {pageCount}
        </span>
        <button type="button" className={btn} onClick={() => onChange(page + 1)} disabled={page >= pageCount - 1}>
          {t('kpis.aiCoverage.pager.next')}
        </button>
      </div>
    </div>
  );
};

const InlineBar: React.FC<{ value: number; color: string }> = ({ value, color }) => (
  <div className="flex items-center gap-2">
    <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
      <div className="h-full rounded-full" style={{ width: `${Math.min(100, value)}%`, backgroundColor: color }} />
    </div>
    <span className="w-12 shrink-0 text-right text-xs font-semibold text-gray-700 dark:text-gray-200 tabular-nums">
      {value.toFixed(1)}%
    </span>
  </div>
);

function pickSort(row: NormalizedRow, key: SortKey, confidence: ConfidenceMode): number | string {
  switch (key) {
    case 'name': return row.display_name;
    case 'group': return row.group;
    case 'studies_percent': return confidence === 'high' ? row.studies_percent_high : row.studies_percent_any;
    case 'videos_percent': return row.videos_percent;
    case 'frames_any': return row.frames_percent_any;
    case 'frames_high': return row.frames_percent_high;
    case 'mean_score': return row.mean_score ?? -1;
    case 'det_per_frame': return (confidence === 'high' ? row.det_per_frame_high : row.det_per_frame_any) ?? -1;
    case 'detections': return confidence === 'high' ? row.detections_high : row.detections_any;
    case 'best_sweep': {
      const bs = confidence === 'high' ? row.best_sweep_high : row.best_sweep_any;
      return bs?.hit_rate ?? -1;
    }
    default: return 0;
  }
}

// ---------------- sections F/G: heatmap ----------------

const HeatmapView: React.FC<{
  heatmap: NormalizedHeatmap;
  structures: NormalizedRow[];
  confidence: ConfidenceMode;
  columnHeader: string;
}> = ({ heatmap, structures, confidence, columnHeader }) => {
  const { t } = useTranslation('Dashboard');
  const [page, setPage] = useState(0);
  const [groupFilter, setGroupFilter] = useState<string>('all');
  const [search, setSearch] = useState('');
  const HEATMAP_PAGE_SIZE = 12;

  React.useEffect(() => {
    setPage(0);
  }, [groupFilter, search]);

  const flatRows = useMemo(() => {
    const structByRow = new Map<string | number, NormalizedRow>();
    structures.forEach((s) => {
      if (s.code) structByRow.set(s.code, s);
      if (s.category_id != null) structByRow.set(s.category_id, s);
    });
    const known = heatmap.rows
      .map((r) => ({ raw: r, struct: structByRow.get(r.category_id) }))
      .filter((x) => !!x.struct)
      .filter((x) => filterStructureRow((x as any).struct, groupFilter, search)) as {
        raw: typeof heatmap.rows[number];
        struct: NormalizedRow;
      }[];

    const byGroup = new Map<string, typeof known>();
    known.forEach((row) => {
      const key = row.struct.group || 'other';
      const arr = byGroup.get(key) || [];
      arr.push(row);
      byGroup.set(key, arr);
    });
    byGroup.forEach((rows) =>
      rows.sort((a, b) => b.struct.studies_percent_any - a.struct.studies_percent_any)
    );
    const groups = Array.from(byGroup.entries()).sort((a, b) => {
      const meanA = a[1].reduce((s, r) => s + r.struct.studies_percent_any, 0) / a[1].length;
      const meanB = b[1].reduce((s, r) => s + r.struct.studies_percent_any, 0) / b[1].length;
      return meanB - meanA;
    });
    return groups.flatMap(([, rows]) => rows);
  }, [heatmap.rows, structures, groupFilter, search]);

  const totalRows = flatRows.length;
  const pageCount = Math.max(1, Math.ceil(totalRows / HEATMAP_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);

  const pageGroups = useMemo(() => {
    const pageRows = flatRows.slice(
      safePage * HEATMAP_PAGE_SIZE,
      (safePage + 1) * HEATMAP_PAGE_SIZE
    );
    const m = new Map<string, typeof pageRows>();
    pageRows.forEach((r) => {
      const g = r.struct.group || 'other';
      const arr = m.get(g) || [];
      arr.push(r);
      m.set(g, arr);
    });
    return Array.from(m.entries());
  }, [flatRows, safePage]);

  if (!heatmap.columns.length || !heatmap.rows.length) {
    return <NoDataPlaceholder className="h-48" message={t('kpis.aiCoverage.heatmap.empty')} />;
  }

  const anyLowN = heatmap.columns.some((c) => c.n < LOW_N_FOOTNOTE);
  // stretch columns to fill the panel when they fit; scroll only past the threshold
  const COL_FIT_THRESHOLD = 10;
  const scrollColumns = heatmap.columns.length > COL_FIT_THRESHOLD;
  const structColWidth = 220;
  // when the panel is very wide relative to column count, keep tiles from
  // becoming letterbox bars by growing the row height with cell width.
  // rough heuristic: taller rows for fewer columns.
  const cellHeight = heatmap.columns.length <= 4 ? 56 : heatmap.columns.length <= 6 ? 48 : 40;

  const tableStyle: React.CSSProperties = scrollColumns
    ? {
        minWidth: structColWidth + heatmap.columns.length * 108,
        borderCollapse: 'separate',
        borderSpacing: 0,
        width: '100%',
      }
    : { width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: 0 };

  return (
    <div className="space-y-3">
      <StructureFilters
        structures={structures}
        groupFilter={groupFilter}
        onGroupChange={setGroupFilter}
        search={search}
        onSearchChange={setSearch}
      />
      {totalRows === 0 ? (
        <NoDataPlaceholder className="h-40" message={t('kpis.aiCoverage.table.empty')} />
      ) : (
      <div className="space-y-3">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800">
        <div className={scrollColumns ? 'overflow-x-auto' : ''}>
          <table className="text-xs" style={tableStyle}>
            <colgroup>
              <col style={{ width: structColWidth }} />
              {heatmap.columns.map((col) => (
                <col key={col.key} style={scrollColumns ? { minWidth: 108 } : undefined} />
              ))}
            </colgroup>
            <thead>
              <tr>
                <th
                  className="px-3 py-2 text-left text-[11px] uppercase tracking-wide font-semibold text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700 sticky left-0 z-20"
                  style={{ width: structColWidth }}
                >
                  {columnHeader}
                </th>
                {heatmap.columns.map((col) => (
                  <th
                    key={col.key}
                    className="px-1 py-2 text-center text-[11px] font-semibold text-gray-600 dark:text-gray-300 align-bottom bg-gray-50 dark:bg-gray-900/40 border-b border-gray-200 dark:border-gray-700"
                  >
                    <div
                      className="mx-auto whitespace-normal break-words leading-tight"
                      title={col.label}
                    >
                      {col.label}
                    </div>
                    <div className="text-[10px] font-normal text-gray-400 dark:text-gray-500 tabular-nums mt-0.5">
                      n={col.n}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageGroups.map(([groupName, rows], gi) => (
                <React.Fragment key={groupName}>
                  <tr>
                    <td
                      colSpan={heatmap.columns.length + 1}
                      className={`px-3 py-1.5 text-[10px] uppercase tracking-wider font-semibold text-gray-500 dark:text-gray-400 bg-gray-50/60 dark:bg-gray-900/30 ${
                        gi === 0 ? '' : 'border-t border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: groupColor(groupName) }}
                        />
                        {humanizeCap(groupName)}
                        <span className="text-gray-400 dark:text-gray-500 font-normal normal-case tracking-normal">
                          · {rows.length}
                        </span>
                      </span>
                    </td>
                  </tr>
                  {rows.map(({ raw, struct }) => (
                    <tr key={struct.code} className="group">
                      <td
                        className="px-3 py-1 text-left text-[12px] text-gray-700 dark:text-gray-200 whitespace-nowrap bg-white dark:bg-gray-800 sticky left-0 z-10 border-t border-gray-100 dark:border-gray-800 group-hover:bg-gray-50 dark:group-hover:bg-gray-800/60"
                        style={{ width: structColWidth }}
                      >
                        {struct.display_name}
                      </td>
                      {heatmap.columns.map((col) => {
                        const cell = raw.cells.find((c) => c.key === col.key);
                        const pct = cell
                          ? confidence === 'high'
                            ? cell.percent_high
                            : cell.percent_any
                          : 0;
                        const lowN = col.n < LOW_N_FOOTNOTE;
                        const bg = lowN ? '#E5E7EB' : heatColor(pct);
                        const fg = pct > 55 && !lowN ? '#FFFFFF' : '#111827';
                        return (
                          <td
                            key={col.key}
                            className="p-0 border-t border-gray-100 dark:border-gray-800"
                          >
                            <div
                              tabIndex={0}
                              role="gridcell"
                              aria-label={`${struct.display_name} in ${col.label}: ${pct.toFixed(1)}%`}
                              title={`${struct.display_name} — ${col.label}: ${pct.toFixed(1)}%`}
                              style={{
                                backgroundColor: bg,
                                color: fg,
                                margin: 2,
                                height: cellHeight,
                              }}
                              className={`flex items-center justify-center rounded-sm text-[12px] font-semibold tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                                lowN ? 'opacity-70' : ''
                              }`}
                            >
                              {lowN ? '·' : `${Math.round(pct)}`}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Pager
          page={safePage}
          pageCount={pageCount}
          totalCount={totalRows}
          pageSize={HEATMAP_PAGE_SIZE}
          onChange={setPage}
          unit={t('kpis.aiCoverage.pager.structures')}
        />
        <HeatmapLegend />
      </div>
      {anyLowN && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400">
          {t('kpis.aiCoverage.heatmap.lowNFootnote', { n: LOW_N_FOOTNOTE })}
        </p>
      )}
      </div>
      )}
    </div>
  );
};

const HeatmapLegend: React.FC = () => {
  const stops = [0, 20, 40, 60, 80, 100];
  return (
    <div className="flex items-center gap-2 text-[10px] text-gray-500 dark:text-gray-400">
      <span>0%</span>
      <div className="flex h-2.5 w-40 overflow-hidden rounded-sm">
        {stops.map((s) => (
          <div key={s} className="flex-1" style={{ backgroundColor: heatColor(s) }} />
        ))}
      </div>
      <span>100%</span>
    </div>
  );
};

// sequential emerald ramp; starts at a visible tint so 10% is distinct from 0%.
function heatColor(pct: number): string {
  const clamped = Math.max(0, Math.min(100, pct));
  // Lightness maps: 0% -> 94%, 100% -> 26%
  const light = 94 - (clamped / 100) * 68;
  // Saturation ramps a bit as coverage grows for legibility
  const sat = 45 + (clamped / 100) * 30;
  return `hsl(160, ${sat}%, ${light}%)`;
}

function groupColor(group: string): string {
  const map: Record<string, string> = {
    cardiac: '#DC2626',
    brain: '#7C3AED',
    extremities: '#D97706',
    abdominal: '#059669',
    biometry: '#2563EB',
    environment: '#0891B2',
    face: '#DB2777',
    maternal: '#9333EA',
    thoracic: '#EA580C',
    spine: '#0284C7',
  };
  return map[group] || '#64748B';
}

function humanizeCap(key: string): string {
  return key.charAt(0).toUpperCase() + key.slice(1);
}

// ---------------- section E: view + measurement panels ----------------

const LIST_PAGE_SIZE = 8;

const ViewCoverageList: React.FC<{ rows: HomeKpisCoverageItem[] }> = ({ rows }) => {
  const { t } = useTranslation('Dashboard');
  const [page, setPage] = useState(0);
  if (!rows.length) {
    return <NoDataPlaceholder className="h-48" message={t('kpis.aiCoverage.viewsPanel.coverageEmpty')} />;
  }
  const pageCount = Math.max(1, Math.ceil(rows.length / LIST_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * LIST_PAGE_SIZE, (safePage + 1) * LIST_PAGE_SIZE);
  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr className="border-b border-gray-200 dark:border-gray-700 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="px-3 py-2 text-left font-semibold min-w-[180px]">{t('kpis.aiCoverage.viewsPanel.colName')}</th>
              <th className="px-3 py-2 text-left font-semibold min-w-[180px]">{t('kpis.charts.coverage')}</th>
              <th className="px-3 py-2 text-right font-semibold">{t('kpis.aiCoverage.viewsPanel.colDetections')}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.name} className="border-t border-gray-100 dark:border-gray-800 text-gray-700 dark:text-gray-200">
                <td className="px-3 py-2 font-medium text-title-page-color-100 dark:text-white">{r.name}</td>
                <td className="px-3 py-2"><InlineBar value={r.coverage_percent} color="#3B82F6" /></td>
                <td className="px-3 py-2 text-right tabular-nums">{r.detections.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager
        page={safePage}
        pageCount={pageCount}
        totalCount={rows.length}
        pageSize={LIST_PAGE_SIZE}
        onChange={setPage}
        unit={t('kpis.aiCoverage.pager.views')}
      />
    </div>
  );
};

const MeasurementList: React.FC<{ rows: HomeKpisAiMeasurementRow[] }> = ({ rows }) => {
  const { t } = useTranslation('Dashboard');
  const [page, setPage] = useState(0);
  if (!rows.length) {
    return <NoDataPlaceholder className="h-48" message={t('kpis.aiCoverage.measurements.empty')} />;
  }
  const pageCount = Math.max(1, Math.ceil(rows.length / LIST_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * LIST_PAGE_SIZE, (safePage + 1) * LIST_PAGE_SIZE);
  return (
    <div className="space-y-2">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr className="border-b border-gray-200 dark:border-gray-700 text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              <th className="px-3 py-2 text-left font-semibold min-w-[220px]">{t('kpis.aiCoverage.measurements.colName')}</th>
              <th className="px-3 py-2 text-left font-semibold min-w-[180px]">{t('kpis.charts.coverage')}</th>
              <th className="px-3 py-2 text-right font-semibold">{t('kpis.aiCoverage.measurements.colDetections')}</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => (
              <tr key={r.code} className="border-t border-gray-100 dark:border-gray-800 text-gray-700 dark:text-gray-200">
                <td className="px-3 py-2">
                  <div className="font-medium text-title-page-color-100 dark:text-white">{r.name}</div>
                </td>
                <td className="px-3 py-2"><InlineBar value={r.coverage_percent} color="#8B5CF6" /></td>
                <td className="px-3 py-2 text-right tabular-nums">{r.detections.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pager
        page={safePage}
        pageCount={pageCount}
        totalCount={rows.length}
        pageSize={LIST_PAGE_SIZE}
        onChange={setPage}
        unit={t('kpis.aiCoverage.pager.measurements')}
      />
    </div>
  );
};

// keep this import used
export type _AiCoverageInternalMarker = EnhancedStructureRow | HomeKpisAiCoverage;
